import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Sparkles,
  Youtube,
  Music2,
  Instagram,
  Facebook,
  FolderSearch,
  FilePlus2,
  Compass,
  ArrowLeft,
  ArrowRight,
  RotateCcw,
  CheckCircle2,
  Info,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Publishing Metadata — UI + simulated UX only.
 *
 * Everything in this file is local component state. Nothing here is ever
 * sent to a backend, an AI provider, or persisted anywhere — it is reset
 * the moment the page refreshes. This is deliberate: this component
 * implements the APPROVED VISUAL + SIMULATED INTERACTION layer only
 * ("Level 1 + Level 2"). Real generation/persistence is a future,
 * separately-scoped phase ("Level 3") and must not be inferred from this
 * file's structure.
 */

const PLATFORMS = [
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'text-red-500' },
  { id: 'tiktok', name: 'TikTok', icon: Music2, color: 'text-pink-500' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'text-purple-500' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'text-blue-500' },
];

const EMPTY_DRAFT = { description: '', hashtags: '', keywords: '' };

const CONTENT_FORMATS = ['Shorts / Reels', 'Long-Form Video', 'Livestream', 'Tutorial', 'Vlog'];
const CONTENT_TONES = ['Casual', 'Professional', 'Energetic', 'Educational', 'Humorous'];

// Purely local, hand-written sample "content" a creator might already have
// authorized — used ONLY to simulate the "Use My Existing Content" picker.
// No platform is ever actually contacted.
const DEMO_CONTENT_SOURCES = {
  youtube: ['Weekly Vlog — Behind the Scenes', 'Full Tutorial: Getting Started', 'Live Q&A Replay'],
  tiktok: ['Quick Tip #1', 'Trending Sound Challenge', 'Day in My Life'],
  instagram: ['Reel: Studio Setup', 'Reel: 3 Lessons I Learned', 'Carousel: Before & After'],
  facebook: ['Community Update Post', 'Livestream Recap', 'Fan Q&A Highlights'],
};

// Hand-written sample variants (NOT AI output) used to simulate "Generate"
// and "Regenerate" — cycles through a fixed local list, nothing else.
const DESCRIPTION_VARIANTS = [
  (topic) => `New content is live!${topic ? ` Today's focus: ${topic}.` : ''} Like, comment, and follow for more just like this.`,
  (topic) => `${topic ? `${topic} — ` : ''}here's everything you need to know. Save this one and share it with a friend!`,
  (topic) => `Another one for the archive.${topic ? ` This time: ${topic}.` : ''} Let me know your thoughts below.`,
];
const HASHTAG_VARIANTS = {
  youtube: ['#youtube #creator #newvideo', '#subscribe #contentcreator #youtuber', '#youtubecommunity #vlog #trending'],
  tiktok: ['#fyp #viral #foryoupage', '#tiktok #trending #creator', '#fyp #contentcreator #viralvideo'],
  instagram: ['#reels #explore #instagood', '#instagram #contentcreator #reelsinstagram', '#explorepage #reels #creator'],
  facebook: ['#facebook #community #creator', '#fbcontent #creator #sharepost', '#facebookcommunity #newpost #creator'],
  shared: ['#content #creator #newpost', '#contentcreator #creatorlife #newrelease', '#creator #community #sharethis'],
};
const KEYWORD_VARIANTS = [
  (topic) => [topic, 'content creator', 'tutorial', 'behind the scenes'].filter(Boolean).join(', '),
  (topic) => [topic, 'how to', 'tips', 'creator tools'].filter(Boolean).join(', '),
  (topic) => [topic, 'highlights', 'community', 'new release'].filter(Boolean).join(', '),
];

const buildSampleDraft = (platformId, variantIndex, topic) => {
  const idx = variantIndex % 3;
  const hashtagPool = HASHTAG_VARIANTS[platformId] || HASHTAG_VARIANTS.shared;
  return {
    platform: platformId,
    description: DESCRIPTION_VARIANTS[idx](topic),
    hashtags: hashtagPool[idx],
    keywords: KEYWORD_VARIANTS[idx](topic),
  };
};

// Small inline tag/chip input shared by the two "topics" fields below —
// kept inside this file since the assignment authorizes exactly one new
// file.
function TagInput({ tags, onChange, placeholder, testId }) {
  const [value, setValue] = useState('');
  const addTag = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (!tags.includes(trimmed)) onChange([...tags, trimmed]);
    setValue('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="bg-white/10 text-white border-white/10 gap-1 pr-1" data-testid={`${testId}-tag-${tag}`}>
            {tag}
            <button type="button" onClick={() => onChange(tags.filter((t) => t !== tag))} className="hover:text-red-400" data-testid={`${testId}-remove-${tag}`}>
              <X className="h-2.5 w-2.5" />
            </button>
          </Badge>
        ))}
      </div>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
          }
        }}
        onBlur={addTag}
        placeholder={placeholder}
        className="h-8 text-xs bg-black/40 border-white/10"
        data-testid={testId}
      />
    </div>
  );
}

// Compact multi-select rendered as toggleable badges — reused for formats,
// tones, and target platforms across all three AI setup paths.
function BadgeMultiSelect({ options, selected, onToggle, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const isOn = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              isOn ? 'bg-fuchsia-500/20 border-fuchsia-500/50 text-fuchsia-300' : 'bg-black/30 border-white/10 text-[#8A93A6] hover:text-white'
            }`}
            data-testid={`${testIdPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export const PublishingMetadataDemo = () => {
  // ── Main Publishing Metadata form state ──
  const [scope, setScope] = useState('shared'); // 'shared' | 'platform'
  const [activePlatform, setActivePlatform] = useState('youtube');
  const [sharedDraft, setSharedDraft] = useState(EMPTY_DRAFT);
  const [platformDrafts, setPlatformDrafts] = useState({
    youtube: { ...EMPTY_DRAFT },
    tiktok: { ...EMPTY_DRAFT },
    instagram: { ...EMPTY_DRAFT },
    facebook: { ...EMPTY_DRAFT },
  });
  const [showSavedNote, setShowSavedNote] = useState(false);

  const currentDraft = scope === 'shared' ? sharedDraft : platformDrafts[activePlatform];
  const updateCurrentDraft = (field, value) => {
    if (scope === 'shared') {
      setSharedDraft((prev) => ({ ...prev, [field]: value }));
    } else {
      setPlatformDrafts((prev) => ({ ...prev, [activePlatform]: { ...prev[activePlatform], [field]: value } }));
    }
  };

  const handleDemoSave = () => {
    toast.info('Demo only — Publishing Metadata is not saved yet. This will connect to real storage in a future update.');
    setShowSavedNote(true);
    setTimeout(() => setShowSavedNote(false), 3000);
  };

  // ── AI Metadata Setup dialog state ──
  const [aiOpen, setAiOpen] = useState(false);
  const [aiStep, setAiStep] = useState('choose-path'); // choose-path | path-existing | path-fresh | path-direction | preview
  const [aiPath, setAiPath] = useState(null);

  // Path A — Use My Existing Content
  const [existingPlatforms, setExistingPlatforms] = useState([]);
  const [existingSources, setExistingSources] = useState([]); // `${platformId}::${sourceName}`

  // Path B — Start Fresh
  const [freshTopics, setFreshTopics] = useState([]);
  const [freshFormats, setFreshFormats] = useState([]);
  const [freshAudience, setFreshAudience] = useState('');
  const [freshPlatforms, setFreshPlatforms] = useState([]);
  const [freshTone, setFreshTone] = useState([]);

  // Path C — Change My Content Direction
  const [directionTopics, setDirectionTopics] = useState([]);
  const [directionAudience, setDirectionAudience] = useState('');
  const [directionPlatforms, setDirectionPlatforms] = useState([]);
  const [directionStyle, setDirectionStyle] = useState('');
  const [retainBranding, setRetainBranding] = useState('yes');

  // Generated sample preview
  const [previewDrafts, setPreviewDrafts] = useState([]); // [{platform, description, hashtags, keywords}]
  const [variantIndex, setVariantIndex] = useState(0);

  const resetAiDialogState = () => {
    setAiStep('choose-path');
    setAiPath(null);
    setPreviewDrafts([]);
    setVariantIndex(0);
  };

  const openAiDialog = () => {
    resetAiDialogState();
    setAiOpen(true);
  };
  const closeAiDialog = () => {
    setAiOpen(false);
    resetAiDialogState();
  };

  const choosePath = (path) => {
    setAiPath(path);
    setAiStep(path === 'existing' ? 'path-existing' : path === 'fresh' ? 'path-fresh' : 'path-direction');
  };

  const toggleInArray = (arr, setArr, value) => {
    setArr(arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);
  };

  const toggleExistingSource = (key) => {
    setExistingSources((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const generatePreview = () => {
    let targetPlatforms = [];
    let topic = '';
    if (aiPath === 'existing') {
      targetPlatforms = existingPlatforms;
      topic = existingPlatforms.length > 0 ? 'your existing content' : '';
    } else if (aiPath === 'fresh') {
      targetPlatforms = freshPlatforms;
      topic = freshTopics.join(', ');
    } else if (aiPath === 'direction') {
      targetPlatforms = directionPlatforms;
      topic = directionTopics.join(', ');
    }
    const platformsToUse = targetPlatforms.length > 0 ? targetPlatforms : ['shared'];
    const drafts = platformsToUse.map((p) => buildSampleDraft(p, 0, topic));
    setVariantIndex(0);
    setPreviewDrafts(drafts);
    setAiStep('preview');
  };

  const regenerate = () => {
    const nextIndex = variantIndex + 1;
    let topic = '';
    if (aiPath === 'existing') topic = 'your existing content';
    if (aiPath === 'fresh') topic = freshTopics.join(', ');
    if (aiPath === 'direction') topic = directionTopics.join(', ');
    setVariantIndex(nextIndex);
    setPreviewDrafts((prev) => prev.map((d) => buildSampleDraft(d.platform, nextIndex, topic)));
    toast.info('Generated a new sample from local demo data.');
  };

  const editPreviewDraft = (index, field, value) => {
    setPreviewDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  };

  const applyPreviewDraft = (draft) => {
    if (draft.platform === 'shared') {
      setSharedDraft({ description: draft.description, hashtags: draft.hashtags, keywords: draft.keywords });
      setScope('shared');
    } else {
      setPlatformDrafts((prev) => ({ ...prev, [draft.platform]: { description: draft.description, hashtags: draft.hashtags, keywords: draft.keywords } }));
      setScope('platform');
      setActivePlatform(draft.platform);
    }
    toast.success(`Applied sample metadata to ${draft.platform === 'shared' ? 'Shared Defaults' : PLATFORMS.find((p) => p.id === draft.platform)?.name} (demo only — not saved)`);
    closeAiDialog();
  };

  const platformLabel = (id) => (id === 'shared' ? 'Shared / General' : PLATFORMS.find((p) => p.id === id)?.name || id);

  return (
    <div className="pt-4 border-t border-white/10" data-testid="publishing-metadata-section">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-fuchsia-400" />
            <span className="text-white font-medium text-sm">Publishing Metadata</span>
            <Badge variant="outline" className="border-fuchsia-500/30 text-fuchsia-300 text-[10px] px-1.5 py-0">UI Demo</Badge>
          </div>
          <p className="text-xs text-[#8A93A6] mt-0.5">Reusable publishing defaults for future content.</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/10 shrink-0"
          onClick={openAiDialog}
          data-testid="ai-generate-metadata-btn"
        >
          <Sparkles className="h-3 w-3 mr-1.5" />
          AI Generate
        </Button>
      </div>

      {/* Scope selector */}
      <RadioGroup
        value={scope}
        onValueChange={setScope}
        className="grid grid-cols-2 gap-2 mb-2"
        data-testid="metadata-scope-selector"
      >
        <label className={`flex items-center gap-2 text-xs rounded-md border px-2.5 py-1.5 cursor-pointer ${scope === 'shared' ? 'bg-cyan-500/10 border-cyan-500/40 text-white' : 'border-white/10 text-[#8A93A6]'}`}>
          <RadioGroupItem value="shared" data-testid="scope-shared-radio" />
          Shared Defaults
        </label>
        <label className={`flex items-center gap-2 text-xs rounded-md border px-2.5 py-1.5 cursor-pointer ${scope === 'platform' ? 'bg-cyan-500/10 border-cyan-500/40 text-white' : 'border-white/10 text-[#8A93A6]'}`}>
          <RadioGroupItem value="platform" data-testid="scope-platform-radio" />
          Platform-Specific Defaults
        </label>
      </RadioGroup>

      {scope === 'platform' && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          {PLATFORMS.map((p) => {
            const Icon = p.icon;
            const isActive = activePlatform === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setActivePlatform(p.id)}
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  isActive ? 'bg-white/10 border-white/30 text-white' : 'border-white/10 text-[#8A93A6] hover:text-white'
                }`}
                data-testid={`platform-tab-${p.id}`}
              >
                <Icon className={`h-3 w-3 ${p.color}`} />
                {p.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Fields */}
      <div className="space-y-2.5" key={scope === 'shared' ? 'shared' : activePlatform}>
        <div>
          <Label className="text-[11px] text-[#8A93A6]">Default Description / CTA</Label>
          <Textarea
            value={currentDraft.description}
            onChange={(e) => updateCurrentDraft('description', e.target.value)}
            placeholder="e.g. New video is up! Like, comment, and follow for more."
            className="text-xs bg-black/40 border-white/10 min-h-[60px] mt-1"
            data-testid="metadata-description-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px] text-[#8A93A6]">Hashtags</Label>
            <Input
              value={currentDraft.hashtags}
              onChange={(e) => updateCurrentDraft('hashtags', e.target.value)}
              placeholder="#creator #content"
              className="h-8 text-xs bg-black/40 border-white/10 mt-1"
              data-testid="metadata-hashtags-input"
            />
          </div>
          <div>
            <Label className="text-[11px] text-[#8A93A6]">Keywords / Tags</Label>
            <Input
              value={currentDraft.keywords}
              onChange={(e) => updateCurrentDraft('keywords', e.target.value)}
              placeholder="tutorial, how-to"
              className="h-8 text-xs bg-black/40 border-white/10 mt-1"
              data-testid="metadata-keywords-input"
            />
          </div>
        </div>
      </div>

      <div className="mt-3">
        <Button
          size="sm"
          variant="outline"
          className="w-full border-white/10 text-[#C0C7D4]"
          onClick={handleDemoSave}
          data-testid="save-publishing-defaults-btn"
        >
          Save Publishing Defaults
        </Button>
        <p className="text-[10px] text-[#555E6E] mt-1 flex items-center gap-1" data-testid="publishing-defaults-demo-note">
          <Info className="h-2.5 w-2.5" />
          {showSavedNote ? 'Not saved — demo only.' : 'Simulated only — does not save or affect your real Brand Kit defaults.'}
        </p>
      </div>

      {/* ── AI Metadata Setup Dialog ── */}
      <Dialog open={aiOpen} onOpenChange={(open) => (open ? setAiOpen(true) : closeAiDialog())}>
        <DialogContent className="bg-[#14171F] border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto" data-testid="ai-metadata-setup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-fuchsia-400" />
              AI Metadata Setup
            </DialogTitle>
            {aiStep === 'choose-path' && (
              <DialogDescription className="text-[#8A93A6]">How would you like to create your publishing metadata?</DialogDescription>
            )}
          </DialogHeader>

          {/* Step: choose a path */}
          {aiStep === 'choose-path' && (
            <div className="space-y-2.5" data-testid="ai-step-choose-path">
              <button
                type="button"
                onClick={() => choosePath('existing')}
                className="w-full text-left p-3 rounded-lg border border-white/10 hover:border-fuchsia-500/40 hover:bg-fuchsia-500/5 transition-colors"
                data-testid="ai-path-existing-btn"
              >
                <div className="flex items-center gap-2 text-sm font-medium"><FolderSearch className="h-4 w-4 text-fuchsia-400" />Use My Existing Content</div>
                <p className="text-xs text-[#8A93A6] mt-1">A future AI feature will analyze your authorized existing content and consolidate reusable descriptions, hashtags, keywords, and tags.</p>
              </button>
              <button
                type="button"
                onClick={() => choosePath('fresh')}
                className="w-full text-left p-3 rounded-lg border border-white/10 hover:border-fuchsia-500/40 hover:bg-fuchsia-500/5 transition-colors"
                data-testid="ai-path-fresh-btn"
              >
                <div className="flex items-center gap-2 text-sm font-medium"><FilePlus2 className="h-4 w-4 text-fuchsia-400" />Start Fresh</div>
                <p className="text-xs text-[#8A93A6] mt-1">Describe the content you plan to create and get suggested metadata built around it.</p>
              </button>
              <button
                type="button"
                onClick={() => choosePath('direction')}
                className="w-full text-left p-3 rounded-lg border border-white/10 hover:border-fuchsia-500/40 hover:bg-fuchsia-500/5 transition-colors"
                data-testid="ai-path-direction-btn"
              >
                <div className="flex items-center gap-2 text-sm font-medium"><Compass className="h-4 w-4 text-fuchsia-400" />Change My Content Direction</div>
                <p className="text-xs text-[#8A93A6] mt-1">Pivoting topics or audience? Describe the new direction without losing what's already working.</p>
              </button>
            </div>
          )}

          {/* Step A: Use My Existing Content */}
          {aiStep === 'path-existing' && (
            <div className="space-y-3" data-testid="ai-step-existing">
              <p className="text-xs text-[#8A93A6] flex items-start gap-1.5">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                This is a simulation — no platform is contacted. Select which connected platforms and sample content items a real version would analyze.
              </p>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1.5 block">Available platforms</Label>
                <div className="flex flex-wrap gap-1.5">
                  {PLATFORMS.map((p) => {
                    const Icon = p.icon;
                    const isOn = existingPlatforms.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleInArray(existingPlatforms, setExistingPlatforms, p.id)}
                        className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${isOn ? 'bg-fuchsia-500/20 border-fuchsia-500/50 text-fuchsia-300' : 'border-white/10 text-[#8A93A6]'}`}
                        data-testid={`existing-platform-${p.id}`}
                      >
                        <Icon className={`h-3 w-3 ${p.color}`} />
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              {existingPlatforms.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[11px] text-[#8A93A6] block">Sample content sources</Label>
                  {existingPlatforms.map((platformId) => (
                    <div key={platformId} className="pl-1">
                      <p className="text-[11px] text-[#C0C7D4] mb-1 capitalize">{platformId}</p>
                      {(DEMO_CONTENT_SOURCES[platformId] || []).map((source) => {
                        const key = `${platformId}::${source}`;
                        return (
                          <label key={key} className="flex items-center gap-2 text-xs text-[#8A93A6] py-0.5 cursor-pointer">
                            <Checkbox checked={existingSources.includes(key)} onCheckedChange={() => toggleExistingSource(key)} data-testid={`existing-source-${key}`} />
                            {source}
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
              <DialogFooterNav
                onBack={() => setAiStep('choose-path')}
                onNext={generatePreview}
                nextDisabled={existingPlatforms.length === 0}
                nextLabel="Generate Preview"
              />
            </div>
          )}

          {/* Step B: Start Fresh */}
          {aiStep === 'path-fresh' && (
            <div className="space-y-3" data-testid="ai-step-fresh">
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1 block">Content topics</Label>
                <TagInput tags={freshTopics} onChange={setFreshTopics} placeholder="Type a topic and press Enter" testId="fresh-topics" />
              </div>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1 block">Content formats</Label>
                <BadgeMultiSelect options={CONTENT_FORMATS} selected={freshFormats} onToggle={(v) => toggleInArray(freshFormats, setFreshFormats, v)} testIdPrefix="fresh-format" />
              </div>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1 block">Target audience</Label>
                <Input value={freshAudience} onChange={(e) => setFreshAudience(e.target.value)} placeholder="e.g. aspiring home cooks in their 20s-30s" className="h-8 text-xs bg-black/40 border-white/10" data-testid="fresh-audience-input" />
              </div>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1 block">Target platforms</Label>
                <BadgeMultiSelect options={PLATFORMS.map((p) => p.name)} selected={freshPlatforms.map((id) => PLATFORMS.find((p) => p.id === id)?.name)} onToggle={(name) => toggleInArray(freshPlatforms, setFreshPlatforms, PLATFORMS.find((p) => p.name === name)?.id)} testIdPrefix="fresh-platform" />
              </div>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1 block">Content style / tone</Label>
                <BadgeMultiSelect options={CONTENT_TONES} selected={freshTone} onToggle={(v) => toggleInArray(freshTone, setFreshTone, v)} testIdPrefix="fresh-tone" />
              </div>
              <DialogFooterNav onBack={() => setAiStep('choose-path')} onNext={generatePreview} nextLabel="Generate Preview" />
            </div>
          )}

          {/* Step C: Change My Content Direction */}
          {aiStep === 'path-direction' && (
            <div className="space-y-3" data-testid="ai-step-direction">
              <p className="text-xs text-[#8A93A6] flex items-start gap-1.5">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                Existing defaults will not be overwritten without your approval — you'll review and choose to apply any suggestion.
              </p>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1 block">New content topics</Label>
                <TagInput tags={directionTopics} onChange={setDirectionTopics} placeholder="Type a topic and press Enter" testId="direction-topics" />
              </div>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1 block">New target audience</Label>
                <Input value={directionAudience} onChange={(e) => setDirectionAudience(e.target.value)} placeholder="e.g. small business owners" className="h-8 text-xs bg-black/40 border-white/10" data-testid="direction-audience-input" />
              </div>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1 block">Target platforms</Label>
                <BadgeMultiSelect options={PLATFORMS.map((p) => p.name)} selected={directionPlatforms.map((id) => PLATFORMS.find((p) => p.id === id)?.name)} onToggle={(name) => toggleInArray(directionPlatforms, setDirectionPlatforms, PLATFORMS.find((p) => p.name === name)?.id)} testIdPrefix="direction-platform" />
              </div>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1 block">Desired content style</Label>
                <Input value={directionStyle} onChange={(e) => setDirectionStyle(e.target.value)} placeholder="e.g. more documentary-style, less scripted" className="h-8 text-xs bg-black/40 border-white/10" data-testid="direction-style-input" />
              </div>
              <div>
                <Label className="text-[11px] text-[#8A93A6] mb-1.5 block">Retain existing branding?</Label>
                <RadioGroup value={retainBranding} onValueChange={setRetainBranding} className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center gap-2 text-xs rounded-md border px-2.5 py-1.5 cursor-pointer ${retainBranding === 'yes' ? 'bg-cyan-500/10 border-cyan-500/40 text-white' : 'border-white/10 text-[#8A93A6]'}`}>
                    <RadioGroupItem value="yes" data-testid="retain-branding-yes" />Yes, keep it
                  </label>
                  <label className={`flex items-center gap-2 text-xs rounded-md border px-2.5 py-1.5 cursor-pointer ${retainBranding === 'no' ? 'bg-cyan-500/10 border-cyan-500/40 text-white' : 'border-white/10 text-[#8A93A6]'}`}>
                    <RadioGroupItem value="no" data-testid="retain-branding-no" />No, start new
                  </label>
                </RadioGroup>
              </div>
              <DialogFooterNav onBack={() => setAiStep('choose-path')} onNext={generatePreview} nextLabel="Generate Preview" />
            </div>
          )}

          {/* Step: Preview */}
          {aiStep === 'preview' && (
            <div className="space-y-3" data-testid="ai-step-preview">
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="border-fuchsia-500/30 text-fuchsia-300 text-[10px]" data-testid="sample-preview-badge">
                  Sample AI-Generated Metadata (Demo — local data only)
                </Badge>
                <Button size="sm" variant="ghost" className="text-[#8A93A6] hover:text-white h-7" onClick={regenerate} data-testid="regenerate-preview-btn">
                  <RotateCcw className="h-3 w-3 mr-1.5" />Regenerate
                </Button>
              </div>
              <div className="space-y-3 max-h-[42vh] overflow-y-auto pr-1">
                {previewDrafts.map((draft, i) => (
                  <Card key={draft.platform} className="bg-black/40 border-white/10">
                    <CardContent className="pt-4 pb-4 space-y-2">
                      <p className="text-xs font-semibold text-white">{platformLabel(draft.platform)}</p>
                      <div>
                        <Label className="text-[10px] text-[#8A93A6]">Description / CTA</Label>
                        <Textarea value={draft.description} onChange={(e) => editPreviewDraft(i, 'description', e.target.value)} className="text-xs bg-black/40 border-white/10 min-h-[50px] mt-1" data-testid={`preview-description-${draft.platform}`} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-[#8A93A6]">Hashtags</Label>
                          <Input value={draft.hashtags} onChange={(e) => editPreviewDraft(i, 'hashtags', e.target.value)} className="h-7 text-xs bg-black/40 border-white/10 mt-1" data-testid={`preview-hashtags-${draft.platform}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-[#8A93A6]">Keywords / Tags</Label>
                          <Input value={draft.keywords} onChange={(e) => editPreviewDraft(i, 'keywords', e.target.value)} className="h-7 text-xs bg-black/40 border-white/10 mt-1" data-testid={`preview-keywords-${draft.platform}`} />
                        </div>
                      </div>
                      <Button size="sm" className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 mt-1" onClick={() => applyPreviewDraft(draft)} data-testid={`apply-draft-${draft.platform}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1.5" />
                        Apply to Publishing Metadata
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Button variant="outline" size="sm" className="w-full border-white/10 text-[#C0C7D4]" onClick={() => setAiStep(aiPath === 'existing' ? 'path-existing' : aiPath === 'fresh' ? 'path-fresh' : 'path-direction')} data-testid="preview-back-btn">
                <ArrowLeft className="h-3 w-3 mr-1.5" />Back to setup
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

function DialogFooterNav({ onBack, onNext, nextDisabled, nextLabel }) {
  return (
    <div className="flex items-center justify-between pt-1">
      <Button variant="ghost" size="sm" className="text-[#8A93A6] hover:text-white" onClick={onBack} data-testid="ai-step-back-btn">
        <ArrowLeft className="h-3 w-3 mr-1.5" />Back
      </Button>
      <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={onNext} disabled={nextDisabled} data-testid="ai-step-next-btn">
        {nextLabel}
        <ArrowRight className="h-3 w-3 ml-1.5" />
      </Button>
    </div>
  );
}

export default PublishingMetadataDemo;
